import { MemoizedSelector, createSelector } from '@ngrx/store'
import { findLast as _findLast, first as _first, last as _last, random as _random } from 'lodash/fp'
import { v4 as uuidV4 } from 'uuid'

import {
  ConversationOutcome,
  ConversationOutcomeClassification,
  ConversationOutcomeNodeConfig,
  ConversationOutcomeState,
  ConversationOutcomeType,
  ConversationThreadExecutionPlanActionState,
  ConversationThreadMessageState,
  ExecutionPlanAction,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { DataModelEntityType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import {
  GeneratedDataModel,
  parseGeneratedDataModel,
} from '@flowx/designer/modules/data-model-builder/utils/generated-data-model.parser'
import {
  dataModelEntitiesToContext,
  selectAllProjectDataModelEntities,
  selectDataModelEntitiesLeaves,
  selectResourceDataModelEntities,
} from '@flowx/designer/shared/store/selectors/data-model.selectors'
import {
  selectDeviceType,
  selectTemplateConfigs,
} from '@flowx/designer/shared/store/selectors/template-config.selectors'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'

import { PanelType } from './ai-conversation.commons'
import { AgentAction, AgentName, UI_FLOW_GENERATION_ACTIONS } from './ai-conversation.commons'
import { ContextDataKeys, OutcomeType } from './ai-conversation.model'
import { ChatEntity } from './chat.entity'
import {
  activeAgentTarget,
  activeAgentThread,
  activeConversation,
  conversationMapKey,
  conversationProcessMap,
  conversationRouteScope,
  conversations,
  defaultThreadId,
  dispatchedUiFlowResource,
  isProcessingOrHasDraft,
  loading,
  mainOutcome,
  mainOutcomes,
  selectFeature,
  selectedProcessId,
  selectedTemplateConfigId,
  selectedUiFlowContainerId,
  selectedUiFlowScopedAreaId,
  selectedUiTemplateContainerId,
  userTaskNavigationAreaId,
} from './conversation-target.selectors'
import {
  assignNewTargetsToCurrentExecutionPlan,
  getDeveloperOutcomeType,
  getExecutionPlanEnhanceDiff,
  hasNonAssistantActions,
  isEmptyExecutionPlan,
  isFinishedThreadState,
  isOneShotAgentAction,
  isUnfinishedMessageWithValidExecutionPlan,
  messageHasValidExecutionPlan,
} from './conversation.utils'
import { ProjectDataModelCatalogueEntry } from './project-data-model-catalogue'

import { AiAnalystUtils } from '../agents/analyst/services/analyst.service'
import { AiTemplateConfig, DesignerService } from '../agents/designer/services/designer.service'
import {
  UiFlowGenerationOutcome,
  buildUiFlowAreaDraftTrees,
  buildUiFlowTemplateTrees,
  createDraftIdentityGenerators,
  dedupeComponentsByKey,
  findUiFlowNodeById,
  normalizeUiFlowOutcome,
  sanitizeUiFlowAiComponents,
  sanitizeUiFlowAnswerAiComponents,
} from '../agents/designer/services/ui-flow-designer.utils'
import {
  developerDataModelChangesToDraft,
  parseDeveloperDataModelChanges,
  reconcileDeveloperDraftWithProcessDm,
} from '../agents/developer/services/developer-data-model.utils'
import {
  AiAgent,
  AiDataModelOutcome,
  AiProcess,
  DataModelDraft,
  FileExtension,
  FileMimeType,
  UploadContextFile,
  imageFileTypes,
} from '../types/ai.types'

const commandCenterActivated = createSelector(
  selectFeature,
  (state) => state.commandCenterActivated
)

const developerContext = createSelector(selectFeature, (state) => state.developerContext)
const target = createSelector(selectFeature, (state) => state.target)
const latestStreamMessage = createSelector(selectFeature, (state) => state.latestStreamMessage)

const activeConversationExists = createSelector(activeConversation, (activeConversation) =>
  Boolean(activeConversation)
)

const activeConversationId = createSelector(
  activeConversation,
  (activeConversation) => activeConversation?.id ?? null
)

const defaultThread = createSelector(
  activeConversation,
  defaultThreadId,
  (activeConversation, defaultThreadId) => {
    if (!activeConversation || !defaultThreadId) {
      return null
    }
    return activeConversation.threads.find((thread) => thread.id === defaultThreadId) ?? null
  }
)

const activeNonDefaultThread = createSelector(
  activeConversation,
  defaultThreadId,
  (activeConversation, defaultThreadId) => {
    if (!activeConversation) {
      return null
    }

    // Find the most recent non-finished thread that is not the default thread
    const nonDefaultThreads = activeConversation.threads.filter(
      (thread) => thread.id !== defaultThreadId && !isFinishedThreadState(thread.state)
    )

    // Return the last (most recent) non-default thread
    return _last(nonDefaultThreads) ?? null
  }
)

const activeNonDefaultThreadId = createSelector(
  activeNonDefaultThread,
  (activeNonDefaultThread) => activeNonDefaultThread?.id ?? null
)

const activeAgentThreadHasPendingOutcome = createSelector(activeAgentThread, (activeAgentThread) =>
  (activeAgentThread?.messages ?? []).some((message) =>
    message.outcome?.outcomes.some(
      (outcome) =>
        outcome.outcomeClassification === ConversationOutcomeClassification.Main &&
        outcome.state === ConversationOutcomeState.Created
    )
  )
)

const activeAgentThreadId = createSelector(
  activeAgentThread,
  (activeAgentThread) => activeAgentThread?.id ?? null
)

const activeAgentThreadExists = createSelector(activeAgentThread, (activeThread) =>
  Boolean(activeThread)
)

const agentProcessingMessage = createSelector(activeAgentThread, (activeAgentThread) => {
  const processingMessage = _findLast(
    (message) => message.state === ConversationThreadMessageState.Processing,
    activeAgentThread?.messages
  )

  if (!processingMessage) {
    return null
  }

  const hasProcessingStateOutcome = processingMessage?.outcome?.outcomes.some(
    (outcome) =>
      outcome.outcomeType !== ConversationOutcomeType.Unknown &&
      outcome.state === ConversationOutcomeState.Unknown
  )

  return hasProcessingStateOutcome || !processingMessage?.outcome?.outcomes?.length
    ? processingMessage
    : null
})

const latestMessageFromAgent = createSelector(
  activeAgentThread,
  (activeAgentThread) => _last(activeAgentThread?.messages) ?? null
)

const lastMessageFromActiveNonDefaultThread = createSelector(
  activeNonDefaultThread,
  (activeNonDefaultThread) => _last(activeNonDefaultThread?.messages) ?? null
)

const latestAgentMessageWithAgentAction = createSelector(activeAgentThread, (activeAgentThread) =>
  activeAgentThread
    ? (_findLast(
        (message) =>
          message.executionPlan?.actions?.[0]?.agent?.agent &&
          message.executionPlan?.actions?.[0]?.agent?.agent !== AgentName.ConnectedGraph,
        activeAgentThread.messages
      ) ?? null)
    : null
)

const MAIN_OUTCOME_TYPES = new Set([
  ConversationOutcomeType.Textual,
  ConversationOutcomeType.NodeConfig,
])

const activeAgentMainOutcomeFinished = createSelector(
  latestAgentMessageWithAgentAction,
  (latestAgentMessage) =>
    latestAgentMessage?.outcome?.outcomes.some(
      (outcome) =>
        MAIN_OUTCOME_TYPES.has(outcome.outcomeType) &&
        outcome.outcomeClassification === ConversationOutcomeClassification.Main &&
        outcome.state === ConversationOutcomeState.Created
    )
)

// The gateway delivers the generated node config as the textual MAIN outcome content — a
// stringified {instructions, context, jsonSchema} payload; typed NODE_CONFIG outcomes stay
// supported should the contract move to structured outcomes. Throws on malformed JSON so the
// result selector can surface it (a non-matching outcome type is a filter, not an error).
const toNodeConfigOutcome = (
  outcome: ConversationOutcome
): ConversationOutcomeNodeConfig | null => {
  if (outcome.outcomeType === ConversationOutcomeType.NodeConfig) {
    return outcome.nodeConfig ?? null
  }

  if (
    outcome.outcomeType === ConversationOutcomeType.Textual &&
    outcome.outcomeClassification === ConversationOutcomeClassification.Main
  ) {
    const parsed = JSON.parse(outcome.textual?.content ?? '')
    const nodeConfig: ConversationOutcomeNodeConfig = {
      instructions: typeof parsed?.instructions === 'string' ? parsed.instructions : null,
      context: typeof parsed?.context === 'string' ? parsed.context : null,
      jsonSchema: typeof parsed?.jsonSchema === 'string' ? parsed.jsonSchema : null,
    }
    return nodeConfig.instructions || nodeConfig.jsonSchema ? nodeConfig : null
  }

  return null
}

// Mirrors aiGeneratedUiOutcomeResult: a pure {data, error} projection so the stream-ended effect
// can raise agentOutcomeParsingFailed on a malformed outcome instead of the card silently vanishing.
const nodeConfigOutcomesResult = createSelector(
  latestAgentMessageWithAgentAction,
  (latestAgentMessage): { data: ConversationOutcomeNodeConfig[]; error: Error | null } => {
    if (
      latestAgentMessage?.executionPlan?.actions?.[0]?.agent?.action !==
      AgentAction.GeneratePromptAndSchema
    ) {
      return { data: [], error: null }
    }

    let error: Error | null = null
    const data = (latestAgentMessage.outcome?.outcomes ?? [])
      .flatMap((outcome) => outcome.multiple?.outcomes ?? [outcome])
      .map((outcome) => {
        try {
          return toNodeConfigOutcome(outcome)
        } catch (e) {
          error ??= e instanceof Error ? e : new Error('Failed to parse node config outcome')
          return null
        }
      })
      .filter((nodeConfig): nodeConfig is ConversationOutcomeNodeConfig => !!nodeConfig)

    return { data, error }
  }
)

const nodeConfigOutcomes = createSelector(nodeConfigOutcomesResult, (result) => result.data)

const nodeConfigParsingError = createSelector(nodeConfigOutcomesResult, (result) => result.error)

const errorOutcomeDetails = createSelector(
  latestAgentMessageWithAgentAction,
  (latestAgentMessage) => {
    const errorOutcome = latestAgentMessage?.outcome?.outcomes.find(
      (outcome) =>
        outcome.outcomeClassification === ConversationOutcomeClassification.Error &&
        outcome.state === ConversationOutcomeState.Created
    )

    if (!errorOutcome) {
      return null
    }

    return {
      hasError: true,
      errorMessage: errorOutcome.textual?.content || 'An error occurred during processing',
    }
  }
)

const firstMessageFromActiveAgent = createSelector(
  activeAgentThread,
  (activeAgentThread) => _first(activeAgentThread?.messages) ?? null
)

const currentMessageAgent = createSelector(activeAgentThread, (activeAgentThread) => {
  const messageWithExecutionPlanAgent = _findLast(
    (message) =>
      message.executionPlan?.actions[0]?.agent &&
      message.executionPlan?.actions[0]?.agent.agent !== AgentName.ConnectedGraph,
    activeAgentThread?.messages
  )

  if (messageWithExecutionPlanAgent) {
    return messageWithExecutionPlanAgent.executionPlan.actions[0]?.agent
  }

  const messageWithUiAgent = _findLast(
    (message) => message.contextData?.items?.some((item) => item.key === ContextDataKeys.UiAgent),
    activeAgentThread?.messages
  )

  if (messageWithUiAgent) {
    return JSON.parse(
      messageWithUiAgent.contextData.items.find((item) => item.key === ContextDataKeys.UiAgent)
        ?.valueGenericString ?? '{}'
    )
  }

  return null
})

const activeAgentFinished = createSelector(
  activeAgentThread,
  (activeAgentThread) =>
    _last(activeAgentThread?.messages)?.state === ConversationThreadMessageState.Finished
)

const agentPendingAction = createSelector(
  latestMessageFromAgent,
  (latestMessageFromAgent) =>
    latestMessageFromAgent?.executionPlan.actions.find(
      (action) => action.state === ConversationThreadExecutionPlanActionState.Pending
    ) ?? null
)

const defaultAgentInActiveAgentThread = createSelector(activeAgentThread, (activeAgentThread) => {
  const defaultAgentItem =
    activeAgentThread?.contextData.items.find((item) => item.key === AgentName.DefaultAgent) ?? null

  return defaultAgentItem?.valueGenericString
    ? JSON.parse(defaultAgentItem.valueGenericString)
    : null
})

const uiHintedAction = createSelector(activeAgentThread, (activeAgentThread) => {
  if (!activeAgentThread?.messages) {
    return null
  }

  // Find the first message that has UI_AGENT context
  const messageWithUiAgent = activeAgentThread.messages.find((message) =>
    message.contextData?.items?.find((item) => item.key === ContextDataKeys.UiAgent)
  )

  if (!messageWithUiAgent) {
    return null
  }

  const uiAgentItem = messageWithUiAgent.contextData.items.find(
    (item) => item.key === ContextDataKeys.UiAgent
  )

  if (uiAgentItem?.valueGenericString) {
    try {
      return JSON.parse(uiAgentItem.valueGenericString)
    } catch (error) {
      console.error('Failed to parse UI_AGENT context:', error)
      return null
    }
  }

  return null
})

const shouldStreamOutcomeToChat = createSelector(latestStreamMessage, (latestStreamMessage) => {
  if (!latestStreamMessage) {
    return false
  }

  return (
    latestStreamMessage.agent.actionId === AgentAction.ExplainCode &&
    latestStreamMessage.agent.agentId === AgentName.AiDeveloper
  )
})

const aiGeneratedProcessOutcome = createSelector(
  mainOutcome,
  currentMessageAgent,
  (mainOutcome, currentMessageAgent) => {
    if (!mainOutcome || !currentMessageAgent) {
      return null
    }

    if (
      currentMessageAgent.agent === AgentName.AiAnalyst &&
      [
        AgentAction.CreateProcess,
        AgentAction.CreateProcessFromImage,
        AgentAction.CreateProcessFromBpmn,
        AgentAction.EditProcess,
      ].includes(currentMessageAgent.action as AgentAction)
    ) {
      try {
        const aiProcess: AiProcess = JSON.parse(mainOutcome)
        const aiEntities = AiAnalystUtils.getAiEntities({ answer: aiProcess })

        return {
          aiEntities,
        }
      } catch (error) {
        console.error('Failed to parse or map AI process outcome:', error)
        return {
          aiEntities: null,
        }
      }
    }

    return null
  }
)

const hasProcessOutcome = createSelector(aiGeneratedProcessOutcome, (outcome) => outcome !== null)

// Diagram-scoped variant of `isProcessingOrHasDraft`. The broad selector locks on ANY unresolved
// outcome (process, UI, data-model, business-rule), but the process diagram only surfaces a
// keep/discard affordance for process outcomes — so a node-scoped draft would freeze the diagram
// with no way out. This narrows the persisted-draft branch to process outcomes only, while still
// locking during active streaming / processing (both transient and self-clearing).
const isProcessingOrHas = (
  hasDraft: MemoizedSelector<object, boolean>
): MemoizedSelector<object, boolean> =>
  createSelector(
    loading,
    activeAgentThread,
    hasDraft,
    (loading, activeAgentThread, hasDraft) =>
      loading ||
      activeAgentThread?.messages.some(
        (message) => message.state === ConversationThreadMessageState.Processing
      ) ||
      hasDraft
  )

const isProcessingOrHasProcessDraft = isProcessingOrHas(hasProcessOutcome)

const architectGeneratedDataModel = createSelector(
  mainOutcome,
  currentMessageAgent,
  (mainOutcome, currentMessageAgent): GeneratedDataModel | null => {
    if (!mainOutcome || !currentMessageAgent) {
      return null
    }

    if (
      currentMessageAgent.agent === AgentName.AiArchitect &&
      [AgentAction.GenerateDataModel, AgentAction.ExtendDataModel].includes(
        currentMessageAgent.action as AgentAction
      )
    ) {
      try {
        const parsed = JSON.parse(mainOutcome)
        return (parsed?.generatedDataModel ?? parsed) as GeneratedDataModel
      } catch (error) {
        console.error('Failed to parse AI architect data model outcome:', error)
        return null
      }
    }

    return null
  }
)

// The architect's current proposal (latest outcome) serialized as DATA_MODEL_CONTEXT, so a follow-up
// makes the agent edit what the user sees rather than the stale thread-open snapshot. The proposal
// is overlaid on the saved model by name, so untouched types — and references to them — survive.
const architectPreviewDataModelContext = createSelector(
  selectAllProjectDataModelEntities,
  architectGeneratedDataModel,
  (savedEntities, proposal) => {
    if (!proposal) {
      return null
    }
    const preview = parseGeneratedDataModel(proposal, DataModelEntityType.PROJECT, savedEntities)
    // Seed the context with user-defined types only. System entities (e.g. the built-in CURRENCY,
    // an object of amount+code) are platform built-ins; handing them to the architect as existing
    // entities makes it echo a spurious currency object back on every follow-up edit (MT-3184).
    // savedEntities is still passed to parseGeneratedDataModel above so proposal references to a
    // system type continue to resolve.
    const byName = new Map(
      savedEntities
        .filter((entity) => entity.type !== DataModelEntityType.SYSTEM)
        .map((entity) => [entity.name, entity])
    )
    preview.entities.forEach((entity) => byName.set(entity.name, entity))
    return dataModelEntitiesToContext([...byName.values()])
  }
)

const UI_GENERATION_ACTIONS = [
  AgentAction.CreateUi,
  AgentAction.CreateUiFromImage,
  AgentAction.EditUi,
  AgentAction.CreateUiTemplate,
  AgentAction.GenerateUiFlow,
  AgentAction.EditUiFlow,
].map((act) => act.toString())

// True when the active designer outcome is an edit (vs a create). The edit preview is the full
// edited UI and must replace the existing UI in place, whereas a create preview is appended.
const isUiEditOutcome = createSelector(
  currentMessageAgent,
  (currentMessageAgent) =>
    currentMessageAgent?.agent === AgentName.AiDesigner &&
    currentMessageAgent.action === AgentAction.EditUi
)

const aiGeneratedUiOutcomeResult = createSelector(
  mainOutcomes,
  currentMessageAgent,
  (outcomes, currentMessageAgent): { data: AiTemplateConfig | null; error: Error | null } => {
    if (
      !currentMessageAgent ||
      currentMessageAgent.agent !== AgentName.AiDesigner ||
      !UI_GENERATION_ACTIONS.includes(currentMessageAgent.action)
    ) {
      return { data: null, error: null }
    }

    const safeParse = (content: string): unknown => {
      try {
        return JSON.parse(content)
      } catch {
        return null
      }
    }
    const data = outcomes.reduce<AiTemplateConfig | null>((acc, outcome) => {
      const parsed = safeParse(outcome.textual?.content ?? '')
      if (parsed && typeof parsed === 'object' && 'components' in parsed) {
        return parsed as AiTemplateConfig
      }
      // The composed ui-flow answer may nest the UI slice under `interface`
      // ({ interface: { interface, components, navigation }, link_buttons }).
      if (UI_FLOW_GENERATION_ACTIONS.has(currentMessageAgent.action as AgentAction)) {
        return normalizeUiFlowOutcome(parsed) ?? acc
      }
      return acc
    }, null)

    return { data, error: null }
  }
)

const aiGeneratedUiOutcome = createSelector(aiGeneratedUiOutcomeResult, (result) => result.data)

const uiOutcomeParsingError = createSelector(aiGeneratedUiOutcomeResult, (result) => result.error)

// Get the UI outcome object
const uiOutcome = createSelector(
  mainOutcomes,
  (mainOutcomes) =>
    mainOutcomes.find((outcome) => {
      try {
        const parsed = JSON.parse(outcome.textual?.content ?? '{}')
        return 'interface' in parsed
      } catch {
        return false
      }
    }) ?? null
)

// Get the data model outcome object
const dataModelOutcome = createSelector(
  mainOutcomes,
  (mainOutcomes) =>
    mainOutcomes.find((outcome) => {
      try {
        const parsed = JSON.parse(outcome.textual?.content ?? '{}')
        return 'mapped_fields' in parsed
      } catch {
        return false
      }
    }) ?? null
)
const aiGeneratedUiDataModelOutcome = createSelector(
  dataModelOutcome,
  currentMessageAgent,
  selectDataModelEntitiesLeaves,
  (dataModelOutcome, currentMessageAgent, dataModelLeaves): AiDataModelOutcome | null => {
    if (!currentMessageAgent) {
      return null
    }

    // Check if this is from the designer agent creating UI
    if (
      currentMessageAgent.agent === AgentName.AiDesigner &&
      UI_GENERATION_ACTIONS.includes(currentMessageAgent.action)
    ) {
      if (!dataModelOutcome?.textual?.content) {
        return null
      }

      try {
        const aiDataModelOutcome: AiDataModelOutcome = JSON.parse(dataModelOutcome.textual.content)

        // Enrich mapped fields with dataModelEntity and filter out fields without a matching leaf.
        // Object/library REFERENCES (isReference) have no data_model_field — they don't map to an
        // existing leaf — so keep them unconditionally; the classifier turns them into OBJECT refs.
        const enrichedMappedFields = (aiDataModelOutcome.mapped_fields || [])
          .map((field) => ({
            ...field,
            dataModelEntity: dataModelLeaves.find(({ name }) => name === field.data_model_field),
          }))
          .filter((field) => field.isReference || field.dataModelEntity !== undefined)

        return {
          ...aiDataModelOutcome,
          mapped_fields: enrichedMappedFields,
        }
      } catch (error) {
        console.error('Failed to parse AI data model outcome:', error)
        return null
      }
    }

    return null
  }
)

// Check if UI outcome is finished (state === Kept)
const isUiOutcomeFinished = createSelector(
  uiOutcome,
  (outcome) => outcome?.state === ConversationOutcomeState.Kept
)

// Check if data model outcome is finished (state === Kept)
const isDataModelOutcomeFinished = createSelector(
  dataModelOutcome,
  (outcome) => outcome?.state === ConversationOutcomeState.Kept
)

const hasUiOutcome = createSelector(aiGeneratedUiOutcome, (outcome) => !!outcome)

// UI designers only preview UI outcomes, so a pending data-model or process draft must not lock
// their canvas, header and sparkle — the same narrowing the diagram already has.
const isProcessingOrHasUiDraft = isProcessingOrHas(hasUiOutcome)

// Read generated data model from thread context (persisted after keep with templateConfigIds)
const generatedDataModelFromContext = createSelector(
  latestAgentMessageWithAgentAction,
  (latestAgentMessageWithAgentAction): DataModelDraft | null => {
    if (!latestAgentMessageWithAgentAction?.contextData?.items) {
      return null
    }

    const dataModelItem = latestAgentMessageWithAgentAction.contextData.items.find(
      (item) => item.key === ContextDataKeys.GeneratedDataModel
    )

    if (!dataModelItem?.valueGenericString) {
      return null
    }

    try {
      return JSON.parse(dataModelItem.valueGenericString) as DataModelDraft
    } catch {
      return null
    }
  }
)

// PROJECT/LIBRARY data-type catalogue echoed back on the agent message context (set by the
// context builder). Used to classify generated items that resolve to a project/library object.
const projectDataModelCatalogueFromContext = createSelector(
  latestAgentMessageWithAgentAction,
  (latestAgentMessageWithAgentAction): ProjectDataModelCatalogueEntry[] => {
    const item = latestAgentMessageWithAgentAction?.contextData?.items?.find(
      (item) => item.key === ContextDataKeys.ProjectDataModel
    )

    if (!item?.valueGenericString) {
      return []
    }

    try {
      return JSON.parse(item.valueGenericString) as ProjectDataModelCatalogueEntry[]
    } catch {
      return []
    }
  }
)

const aiGeneratedUiDataModelChanges = createSelector(
  generatedDataModelFromContext,
  aiGeneratedUiOutcome,
  aiGeneratedUiDataModelOutcome,
  selectDataModelEntitiesLeaves,
  projectDataModelCatalogueFromContext,
  currentMessageAgent,
  selectDeviceType,
  (
    fromContext,
    aiGeneratedUiOutcome,
    aiGeneratedUiDataModelOutcome,
    dataModelLeaves,
    projectDataModelCatalogue,
    currentMessageAgent,
    deviceType
  ) => {
    // If we have persisted data model in context, use it (it has templateConfigIds)
    if (fromContext) {
      return fromContext
    }

    // Otherwise, derive from outcomes (during initial generation, before keep)
    if (!aiGeneratedUiOutcome || !aiGeneratedUiDataModelOutcome) {
      return null
    }

    try {
      // Get mapped fields from the data model outcome
      const mappedFields = aiGeneratedUiDataModelOutcome.mapped_fields || []
      // A grouped flow answer carries one component list per edited area, and normalizing keeps
      // only the first one — flatten them all so no area's fields are missed.
      const isUiFlowGeneration =
        !!currentMessageAgent &&
        UI_FLOW_GENERATION_ACTIONS.has(currentMessageAgent.action as AgentAction)
      const groupedAreas = isUiFlowGeneration
        ? ((aiGeneratedUiOutcome as UiFlowGenerationOutcome).areas ?? [])
        : []
      // The apply path sanitizes the answer before building templates (e.g. a form field the AI
      // placed directly in a container is dropped), so the data-model draft must extract from the
      // SAME pruned forest — otherwise it creates attributes and key links for components that
      // were never persisted, and the bulk link fails on entries with no template id.
      const flattenedComponents = groupedAreas.flatMap((area) =>
        sanitizeUiFlowAnswerAiComponents(area.components ?? [], deviceType)
      )
      const config = groupedAreas.length
        ? {
            ...aiGeneratedUiOutcome,
            // Two areas can bind the same field, and extraction emits one item per component —
            // so without this the same attribute is created twice in a single bulk request.
            components: dedupeComponentsByKey(flattenedComponents),
          }
        : isUiFlowGeneration
          ? {
              ...aiGeneratedUiOutcome,
              components: sanitizeUiFlowAnswerAiComponents(
                aiGeneratedUiOutcome.components ?? [],
                deviceType
              ),
            }
          : aiGeneratedUiOutcome
      // Pass current data-model leaves so existing keys the AI didn't echo in mapped_fields are
      // treated as existing (not re-created as duplicate attributes).
      return DesignerService.extractDataModelChangesFromAiConfig(
        config,
        mappedFields,
        dataModelLeaves,
        projectDataModelCatalogue
      )
    } catch (error) {
      console.error('Failed to extract data model changes:', error)
      return null
    }
  }
)

const DEVELOPER_DATA_MODEL_ACTIONS = [
  AgentAction.BusinessRule,
  AgentAction.EditBusinessRule,
  AgentAction.Expression,
  AgentAction.ComputedValue,
].map((act) => act.toString())

const developerDataModelChangesOutcome = createSelector(
  mainOutcomes,
  (mainOutcomes) =>
    mainOutcomes.find(
      (outcome) => parseDeveloperDataModelChanges(outcome.textual?.content) !== null
    ) ?? null
)

const developerDataModelChanges = createSelector(
  developerDataModelChangesOutcome,
  currentMessageAgent,
  selectResourceDataModelEntities,
  (outcome, currentMessageAgent, processEntities): DataModelDraft | null => {
    if (
      currentMessageAgent?.agent !== AgentName.AiDeveloper ||
      !DEVELOPER_DATA_MODEL_ACTIONS.includes(currentMessageAgent.action) ||
      !outcome?.textual?.content
    ) {
      return null
    }
    // Reconcile against the current process DM so already-present keys show as reused and are skipped
    // on apply (the summary and the keep effect both read this selector, keeping them consistent).
    return reconcileDeveloperDraftWithProcessDm(
      developerDataModelChangesToDraft(outcome.textual.content),
      processEntities
    )
  }
)

const pendingMessage = createSelector(selectFeature, (state) => state.pendingMessage)

const messagesWithValidPlansNotFinished = createSelector(defaultThread, (defaultThread) => {
  if (!defaultThread?.messages) {
    return []
  }

  return defaultThread.messages.filter(
    (message) =>
      isUnfinishedMessageWithValidExecutionPlan(message) &&
      hasNonAssistantActions(message.executionPlan)
  )
})

const messageWithNewExecutionPlan = createSelector(
  messagesWithValidPlansNotFinished,
  (messages) => {
    if (messages.length <= 1) {
      return null
    }
    return messages[messages.length - 1] ?? null
  }
)

const messageWithCurrentExecutionPlan = createSelector(
  messagesWithValidPlansNotFinished,
  (messages) => {
    if (messages.length === 0) {
      return null
    }
    if (messages.length === 1) {
      return messages[0] ?? null
    }
    return messages[messages.length - 2] ?? null
  }
)

const newExecutionPlan = createSelector(
  messageWithNewExecutionPlan,
  (message) => message?.executionPlan ?? null
)

const currentExecutionPlan = createSelector(
  messageWithCurrentExecutionPlan,
  (message) => message?.executionPlan ?? null
)

const hasMultiplePendingExecutionPlansOnDefaultThread = createSelector(
  defaultThread,
  messagesWithValidPlansNotFinished,
  (defaultThread, messagesWithValidPlansNotFinished) => {
    if (!defaultThread?.messages) {
      return false
    }

    return messagesWithValidPlansNotFinished.length >= 2
  }
)

const enhanceExecutionPlanDiff = createSelector(
  hasMultiplePendingExecutionPlansOnDefaultThread,
  currentExecutionPlan,
  newExecutionPlan,
  (hasMultiplePlans, currentExecutionPlan, newExecutionPlan) => {
    if (
      !hasMultiplePlans ||
      isEmptyExecutionPlan(currentExecutionPlan) ||
      isEmptyExecutionPlan(newExecutionPlan)
    ) {
      return null
    }

    const filterAssistant = (actions: ExecutionPlanAction[]): ExecutionPlanAction[] =>
      actions.filter((action: ExecutionPlanAction) => action.agent?.agent !== AgentName.AiAssistant)

    // Pass the execution plan actions to the utility function
    const diff = getExecutionPlanEnhanceDiff(
      filterAssistant(currentExecutionPlan?.actions || []),
      filterAssistant(newExecutionPlan?.actions || [])
    )

    // Check if the diff is empty (all diff arrays are empty)
    const diffEmpty =
      diff.added.length === 0 && diff.updated.length === 0 && diff.deleted.length === 0

    return diffEmpty ? null : diff
  }
)

const chatMessages = createSelector(
  activeConversation,
  pendingMessage,
  hasMultiplePendingExecutionPlansOnDefaultThread,
  enhanceExecutionPlanDiff,
  aiGeneratedUiDataModelChanges,
  isUiOutcomeFinished,
  selectedProcessId,
  developerDataModelChanges,
  (
    conversation,
    pendingMessage,
    hasMultiplePlans,
    enhancePlanDiff,
    dataModelChanges,
    isUiOutcomeFinished,
    selectedProcessId,
    developerDataModelChanges
  ) =>
    ChatEntity.buildChatHistory(
      conversation,
      pendingMessage,
      hasMultiplePlans,
      enhancePlanDiff,
      dataModelChanges,
      isUiOutcomeFinished,
      selectedProcessId !== null,
      developerDataModelChanges
    )
)

const currentOutcomeBeingKept = createSelector(
  currentMessageAgent,
  isUiOutcomeFinished,
  uiOutcome,
  dataModelOutcome,
  activeAgentThread,
  (agent, isUiFinished, uiOutcome, dataModelOutcome, activeAgentThread): OutcomeType | null => {
    if (!agent) {
      return null
    }

    switch (agent.agent) {
      case AgentName.AiAnalyst:
        return OutcomeType.Process

      case AgentName.AiDesigner:
        // Two-step keep flow: UI first, then Data Model
        if (!isUiFinished && uiOutcome) {
          return OutcomeType.UI
        }
        if (isUiFinished && dataModelOutcome) {
          return OutcomeType.DataModel
        }
        return OutcomeType.UI // Default to UI if no outcomes yet

      case AgentName.AiDeveloper:
        // For most actions, derive from action type
        // For ImproveCode/DocumentCode/FixCode, read from context
        return getDeveloperOutcomeType(agent.action, activeAgentThread)

      default:
        return null
    }
  }
)

const lastDefaultThreadMessageWithValidPlan = createSelector(defaultThread, (defaultThread) => {
  if (!defaultThread?.messages) {
    return null
  }

  return _findLast(messageHasValidExecutionPlan, defaultThread.messages)
})

const activeAgentThreadIsExecutionPlanChild = createSelector(
  activeAgentThread,
  lastDefaultThreadMessageWithValidPlan,
  (activeAgentThread, defaultPlanMessage) => {
    const actionId = activeAgentThread?.messages?.[0]?.executionPlan?.actions?.[0]?.localId
    if (!actionId || !defaultPlanMessage) {
      return false
    }
    return Boolean(
      defaultPlanMessage.executionPlan.actions.find((action) => action.localId === actionId)
    )
  }
)

const shouldKeepThreadOpenOnError = createSelector(
  activeAgentThreadId,
  activeAgentThreadIsExecutionPlanChild,
  currentMessageAgent,
  (activeAgentThreadId, isExecutionPlanChild, currentMessageAgent) =>
    Boolean(activeAgentThreadId) &&
    !isExecutionPlanChild &&
    !isOneShotAgentAction(currentMessageAgent)
)

const defaultThreadExists = createSelector(defaultThread, (defaultThread) => Boolean(defaultThread))

const conversationExists = createSelector(activeConversation, (activeConversation) =>
  Boolean(activeConversation)
)

const currentExecutionPlanExists = createSelector(
  currentExecutionPlan,
  (currentExecutionPlan) =>
    currentExecutionPlan &&
    !isEmptyExecutionPlan(currentExecutionPlan) &&
    hasNonAssistantActions(currentExecutionPlan)
)

const executionPlanActions = createSelector(
  currentExecutionPlan,
  (currentExecutionPlan) => currentExecutionPlan?.actions ?? null
)

const currentExecutionPlanWithUpdatedTargets = createSelector(
  currentExecutionPlan,
  newExecutionPlan,
  (currentExecutionPlan, newExecutionPlan) => {
    if (!currentExecutionPlan) {
      return null
    }

    if (!newExecutionPlan) {
      return {
        executionPlan: currentExecutionPlan,
        updatedActions: new Set<string>(),
      }
    }

    return assignNewTargetsToCurrentExecutionPlan(currentExecutionPlan, newExecutionPlan)
  }
)

const aiGeneratedNodes = createSelector(selectFeature, (state) => state.aiGeneratedNodes)

const showContextDocuments = createSelector(selectFeature, (state) =>
  state.panelStack.includes(PanelType.DOCUMENT_CONTEXT)
)

const selectedDocument = createSelector(selectFeature, (state) => state.selectedDocument)

const showDocumentResources = createSelector(selectFeature, (state) =>
  state.panelStack.includes(PanelType.DOCUMENT_RESOURCES)
)

const newDocumentsCount = createSelector(selectFeature, (state) => state.newDocumentsCount)

const uploadedDocumentIds = createSelector(selectFeature, (state) => state.uploadedDocumentIds)

// Keep the original hint as well as the current action: older image threads may
// already have had their flow action rewritten to create_ui_from_image.
const attachmentsAllowed = createSelector(
  uiHintedAction,
  currentMessageAgent,
  (hintedAction, currentAgent) =>
    ![hintedAction, currentAgent].some(
      (agent) =>
        agent?.agent === AgentName.AiDesigner &&
        UI_FLOW_GENERATION_ACTIONS.has(agent.action as AgentAction)
    )
)

// File upload configuration selectors
const fileUploadConfig = createSelector(selectFeature, (state) => state.fileUploadConfig)

const allowedImageFileTypes = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.imageUpload?.fileType || []
)

const allowedBpmnFileTypes = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.bpmnUpload?.fileType || []
)

const maxAllowedBpmnFileSize = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.bpmnUpload?.maxFileSize
)

const maxAllowedImageFiles = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.imageUpload?.maxFileNumber || 1
)

const maxAllowedImageFileSize = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.imageUpload?.maxFileSize
)

const isMultipleImageUploadAllowed = createSelector(
  maxAllowedImageFiles,
  (maxAllowedImageFiles) => maxAllowedImageFiles > 1
)

const allowedDocumentFileTypes = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.documentUpload?.fileType || []
)

const maxAllowedDocumentFiles = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.documentUpload?.maxFileNumber || 1
)

const isMultipleDocumentUploadAllowed = createSelector(
  maxAllowedDocumentFiles,
  (maxAllowedDocumentFiles) => maxAllowedDocumentFiles > 1
)

const uiHintedAgent = createSelector(uiHintedAction, (uiHintedAction) => {
  // Use the agent from UI_AGENT context if available
  if (uiHintedAction?.agent) {
    // Map the agent name from context to AiAgent enum
    switch (uiHintedAction.agent) {
      case AgentName.AiAnalyst:
        return AiAgent.ANALYST
      case AgentName.AiDesigner:
        return AiAgent.DESIGNER
      case AgentName.AiDeveloper:
        return AiAgent.DEVELOPER
      default:
        return AiAgent.ASSISTANT
    }
  }
  return AiAgent.ASSISTANT
})

const agentFileUploadConfig = createSelector(
  fileUploadConfig,
  uiHintedAgent,
  (fileUploadConfig, agent) =>
    fileUploadConfig?.fileUpload?.[agent] || fileUploadConfig?.fileUpload?.default
)

const allowedFileTypes = createSelector(
  agentFileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.fileType || []
)

const maxAllowedFiles = createSelector(
  agentFileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.maxFileNumber || 1
)

const isMultipleUploadAllowed = createSelector(
  maxAllowedFiles,
  (maxAllowedFiles) => maxAllowedFiles > 1
)

// Uploaded files selectors
const uploadedFiles = createSelector(selectFeature, (state) => state.uploadedFiles)

const uploadedImageFiles = createSelector(uploadedFiles, (uploadedFiles) =>
  uploadedFiles.filter((file): file is UploadContextFile =>
    imageFileTypes.includes(file.type as FileMimeType)
  )
)

const uploadedBpmnFiles = createSelector(uploadedFiles, (uploadedFiles) =>
  uploadedFiles.filter(
    (file): file is UploadContextFile =>
      file.type === FileMimeType.BPMN || file.name.endsWith(FileExtension.BPMN)
  )
)

const hasUploadedFiles = createSelector(uploadedFiles, (uploadedFiles) => !!uploadedFiles.length)

// For now, create a simple isUploadDisabled selector
// This will need to be updated based on actual business logic
const isUploadDisabled = createSelector(
  hasUploadedFiles,
  loading,
  (hasUploadedFiles, loading) => hasUploadedFiles || loading
)

const addDefaultThreadMessageStatus = createSelector(
  selectFeature,
  (state) => state.addDefaultThreadMessageStatus
)

const assigningPagesToUserTasks = createSelector(
  selectFeature,
  (state) => state.assigningPagesToUserTasks
)

const keepDiscardOutcomeState = createSelector(
  selectFeature,
  (state) => state.keepDiscardOutcomeState
)

const aiGeneratedUi = createSelector(
  aiGeneratedUiOutcome,
  isUiOutcomeFinished,
  (aiGeneratedUiOutcome, isFinished) => {
    // Return null if UI outcome is finished (kept)
    if (isFinished) {
      return null
    }

    if (!aiGeneratedUiOutcome) {
      return null
    }

    try {
      return DesignerService.mapAiUiToTemplateConfig(
        () => _random(Date.now(), Number.MAX_SAFE_INTEGER),
        uuidV4,
        aiGeneratedUiOutcome
      ) as TemplateConfigInterface
    } catch (error) {
      console.error('Failed to map AI ui outcome:', error)
      return null
    }
  }
)

// A sparkle-scoped grouped answer previews IN PLACE (buildUiFlowEditAreaPreview); an unscoped
// one previews as a draft forest. Both gates read this one definition, so the two paths can
// never both claim — or both drop — the same answer.
const isUiFlowScopedGroupedAnswer = createSelector(
  aiGeneratedUiOutcome,
  selectedUiFlowScopedAreaId,
  (outcome, scopedAreaId) =>
    !!(outcome as UiFlowGenerationOutcome | null)?.areas?.length && scopedAreaId != null
)

// The pending whole-flow draft as one tree per navigation area, sanitized for the canvas
// device type. `draft` on the roots only — nesting the flag stacks preview frames.
const aiGeneratedUiFlowAreas = createSelector(
  aiGeneratedUiOutcome,
  currentMessageAgent,
  isUiOutcomeFinished,
  selectDeviceType,
  selectedUiFlowScopedAreaId,
  selectTemplateConfigs,
  isUiFlowScopedGroupedAnswer,
  (
    outcome,
    currentMessageAgent,
    isFinished,
    deviceType,
    scopedAreaId,
    templateConfigs,
    scopedGroupedAnswer
  ): TemplateConfigInterface[] => {
    if (
      isFinished ||
      !outcome ||
      !currentMessageAgent ||
      !UI_FLOW_GENERATION_ACTIONS.has(currentMessageAgent.action as AgentAction) ||
      scopedGroupedAnswer
    ) {
      return []
    }

    const areas = (outcome as UiFlowGenerationOutcome).areas ?? []

    // A scoped answer is rooted at the edited element, which may not be a legal flow root
    // (a TAB or MODAL) — widen the sanitizer's root rule to it.
    const scopedArea =
      scopedAreaId != null ? findUiFlowNodeById(templateConfigs ?? [], scopedAreaId) : null

    try {
      // Deterministic ids/uuids: this selector recomputes on unrelated store changes, and fresh
      // random identities would recreate the rendered draft subtree (resetting tab selection).
      const { nextId, nextFlowxUuid } = createDraftIdentityGenerators()

      // A typed follow-up answers with the whole flow grouped by area — each area becomes
      // its own draft tree, so the forest previews like a generation would.
      const trees = areas.length
        ? areas.flatMap(
            (area) =>
              buildUiFlowAreaDraftTrees(
                area,
                templateConfigs ?? [],
                outcome.interface,
                deviceType,
                nextId,
                nextFlowxUuid
              ).trees
          )
        : buildUiFlowTemplateTrees(
            sanitizeUiFlowAiComponents(
              outcome.components,
              deviceType,
              scopedArea ? [scopedArea.componentIdentifier] : []
            ),
            outcome.interface,
            nextId,
            nextFlowxUuid
          )
      return trees.map((tree) => ({ ...tree, draft: true }))
    } catch (error) {
      console.error('Failed to map AI ui-flow outcome:', error)
      return []
    }
  }
)

export const ConversationSelectors = {
  attachmentsAllowed,
  commandCenterActivated,

  activeAgentThreadId,
  activeAgentThreadExists,
  activeAgentThreadHasPendingOutcome,
  activeAgentFinished,
  activeAgentMainOutcomeFinished,
  nodeConfigOutcomes,
  nodeConfigParsingError,
  errorOutcomeDetails,
  mainOutcome,
  aiGeneratedUiOutcome,
  uiOutcomeParsingError,
  aiGeneratedUiDataModelOutcome,
  aiGeneratedUi,
  aiGeneratedUiFlowAreas,
  isUiFlowScopedGroupedAnswer,
  hasUiOutcome,
  isUiEditOutcome,
  aiGeneratedUiDataModelChanges,
  developerDataModelChanges,
  projectDataModelCatalogueFromContext,
  generatedDataModelFromContext,
  aiGeneratedProcessOutcome,
  hasProcessOutcome,
  architectGeneratedDataModel,
  architectPreviewDataModelContext,
  firstMessageFromActiveAgent,
  latestMessageFromAgent,
  latestAgentMessageWithAgentAction,
  activeAgentTarget,
  currentMessageAgent,
  activeConversation,
  activeConversationId,
  activeConversationExists,
  conversations,
  conversationProcessMap,
  conversationMapKey,
  conversationRouteScope,
  selectedProcessId,
  defaultThread,
  defaultThreadId,
  chatMessages,
  pendingMessage,
  developerContext,
  target,
  activeAgentThread,
  activeNonDefaultThread,
  activeNonDefaultThreadId,
  lastMessageFromActiveNonDefaultThread,
  agentProcessingMessage,
  agentPendingAction,
  defaultAgentInActiveAgentThread,
  uiHintedAction,
  shouldStreamOutcomeToChat,
  latestStreamMessage,
  loading,
  isProcessingOrHasDraft,
  isProcessingOrHasProcessDraft,
  isProcessingOrHasUiDraft,
  currentOutcomeBeingKept,
  userTaskNavigationAreaId,
  selectedTemplateConfigId,
  selectedUiFlowContainerId,
  dispatchedUiFlowResource,
  selectedUiFlowScopedAreaId,
  selectedUiTemplateContainerId,
  lastDefaultThreadMessageWithValidPlan,
  activeAgentThreadIsExecutionPlanChild,
  shouldKeepThreadOpenOnError,
  hasMultiplePendingExecutionPlansOnDefaultThread,
  enhanceExecutionPlanDiff,
  messagesWithValidPlansNotFinished,
  messageWithNewExecutionPlan,
  messageWithCurrentExecutionPlan,
  newExecutionPlan,
  currentExecutionPlan,
  currentExecutionPlanWithUpdatedTargets,
  executionPlanActions,
  defaultThreadExists,
  conversationExists,
  currentExecutionPlanExists,
  aiGeneratedNodes,
  showContextDocuments,
  selectedDocument,
  showDocumentResources,
  newDocumentsCount,
  uploadedDocumentIds,
  // File upload configuration selectors
  fileUploadConfig,
  allowedImageFileTypes,
  allowedBpmnFileTypes,
  isMultipleImageUploadAllowed,
  maxAllowedImageFileSize,
  maxAllowedBpmnFileSize,
  allowedDocumentFileTypes,
  isMultipleDocumentUploadAllowed,
  uiHintedAgent,
  agentFileUploadConfig,
  allowedFileTypes,
  isMultipleUploadAllowed,
  // Uploaded files selectors
  uploadedFiles,
  uploadedImageFiles,
  uploadedBpmnFiles,
  hasUploadedFiles,
  isUploadDisabled,
  keepDiscardOutcomeState,
  addDefaultThreadMessageStatus,
  assigningPagesToUserTasks,
  // Multiple outcomes selectors
  uiOutcome,
  dataModelOutcome,
  isUiOutcomeFinished,
  isDataModelOutcomeFinished,
}
